import React, { CSSProperties, AnchorHTMLAttributes, useEffect } from 'react';
import cx from 'classnames';
import styles from './Card.less';
import labelStyles from './CardLabel.less';
import badgeStyles from './Badge.less';
import avatarStyles from '../avatar/Avatar.less';
import { UnsplashTracker } from '@trello/unsplash';

interface CardProps {
  className?: string;
  style?: CSSProperties;
}

export const Card: React.FC<CardProps> = ({ className, children, style }) => (
  <div className={cx(styles.card, className)} style={style}>
    {children}
  </div>
);

interface LinkComponentProps
  extends CardProps,
    AnchorHTMLAttributes<HTMLAnchorElement> {}

interface CardLinkProps extends CardProps {
  href?: string;
  onClick?: () => void;
  linkComponent?:
    | React.FC<LinkComponentProps>
    | React.ComponentClass<LinkComponentProps>;
}

export const CardLink: React.FC<CardLinkProps> = ({
  linkComponent: LinkComponent,
  className,
  ...props
}) => {
  if (LinkComponent) {
    return (
      <LinkComponent
        className={cx(className, styles.card, styles.cardLink)}
        {...props}
      />
    );
  }
  return (
    <a className={cx(className, styles.card, styles.cardLink)} {...props} />
  );
};

interface CardStickersProps {
  height?: number;
}

export const CardStickers: React.FC<CardStickersProps> = ({
  height,
  children,
}) => (
  <div
    className={styles.stickers}
    style={height ? { height: `${height}px` } : undefined}
  >
    {children}
  </div>
);

interface CardCoverProps {
  bgColor?: string;
  img: string;
  bgSize?: 'cover' | 'contain';
  height: number;
}

export const CardCover: React.FC<CardCoverProps> = ({
  img,
  height,
  bgSize,
  bgColor,
  children,
}) => {
  useEffect(
    () => {
      UnsplashTracker.trackOncePerInterval(img);
    },
    [img],
  );

  const dynamicStyles: CSSProperties = {
    height: `${height}px`,
    backgroundImage: `url(${img})`,
  };

  if (bgColor) {
    dynamicStyles.backgroundColor = bgColor;
  }

  if (bgSize) {
    dynamicStyles.backgroundSize = bgSize;
  }

  return (
    <div className={styles.cover} style={dynamicStyles}>
      {children}
    </div>
  );
};

export const CardDetails: React.FC = ({ children }) => (
  <div className={styles.details}>{children}</div>
);

export const CardTitle: React.FC = ({ children }) => (
  <div className={styles.title}>{children}</div>
);

export const CardLabels: React.FC = ({ children }) => (
  <div className={cx(styles.labels, labelStyles.cardFront)}>{children}</div>
);

export const CardBadges: React.FC = ({ children }) => (
  <div className={cx(styles.badges, badgeStyles.cardFront)}>{children}</div>
);

export const CardMembers: React.FC = ({ children }) => (
  <div className={cx(styles.members, avatarStyles.cardFront)}>{children}</div>
);
